import { useMemo, useState, type FC, type Key } from "react";
import { Button, notification } from "antd";
import { CirclePlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import ConfirmationModal from "src/components/reusable/ConfirmationModal";
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import { TablePagePagination } from "src/components/common";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import { CustomIcon } from "src/components/reusable/CustomIcon";
import { formatNumber } from "src/utils/numberFormat";
import UserFilters from "./Components/UserFilters";
import UserFormModal from "./Components/UserFormModal";
import { getUserTableColumns } from "./Components/UserTableColumns";
import { useTokenSession } from "./Hooks/UseTokenSession";
import { useUserFilters } from "./Hooks/UseUserFilters";
import { useUsers } from "./Hooks/UseUsers";
import type { User } from "./Types/UserTypes";
import { IsAdminUser, IsUserLimitReached, IsUserLocked } from "./Utils/UserPermissions";

const DELETE_ICON_URL = "/images/delete.png";

const UserManagement: FC = () => {
  const { t, i18n } = useTranslation();
  const isArabic = Boolean(i18n.language?.startsWith("ar"));
  useTokenSession();
  const {
    users,
    isLoading,
    isSocialListeningEnabled,
    deletingUserId,
    apiKey,
    tenantId,
    toggleStatus,
    changeAccessLevel,
    removeUser,
    removeUsers,
    addUser,
    replaceUser,
  } = useUsers();
  const filters = useUserFilters(users);

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);

  const isUserLimitReached = IsUserLimitReached(users);
  const hasSelection = selectedRowKeys.length > 0;
  const filteredUsersCount = formatNumber(filters.filteredUsers.length, {}, i18n.language);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
      getCheckboxProps: (record: User) => ({ disabled: IsAdminUser(record) }),
    }),
    [selectedRowKeys]
  );

  const handleAddUser = () => {
    if (isUserLimitReached) {
      notification.error({
        message: t("userManagement.messages.userLimitExceededTitle"),
        description: t("userManagement.messages.userLimitExceededDescription"),
      });
      return;
    }
    setIsAddUserModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setIsEditUserModalOpen(true);
  };

  const handleUpdateUser = (updatedUser: User) => {
    replaceUser(updatedUser);
    setUserToEdit(null);
  };

  const handleBulkDelete = async () => {
    if (await removeUsers(selectedRowKeys)) {
      setSelectedRowKeys([]);
    }
    setIsBulkDeleteOpen(false);
  };

  const handleConfirmDeleteUser = async () => {
    if (userToDelete) {
      await removeUser(userToDelete.id);
      setUserToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="user-management-container">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="user-management-product-header">
        <p className="User-dashboard-header">{t("userManagement.title")}</p>
        <p className="user-management-description">{t("userManagement.description")}</p>
      </div>

      <div className="user-management-box">
        <div className="user-management-card-header">
          <div className="user-management-card-title">
            {t("userManagement.listHeader", { count: filteredUsersCount })}
          </div>

          <div className="user-management-card-actions">
            <span className="user-management-delete-icon-wrap">
              <CustomIcon
                name="delete"
                tooltip={t("userManagement.deleteUserButton")}
                onClick={hasSelection ? () => setIsBulkDeleteOpen(true) : undefined}
                size={18}
                color={hasSelection ? "#2c68ad" : "#141B34"}
                className="user-management-delete-icon"
                style={hasSelection ? { opacity: 1, cursor: "pointer" } : { opacity: 0.45, cursor: "not-allowed" }}
              />
            </span>

            <StyledTooltip
              content={isUserLimitReached ? t("userManagement.maxUserLimitReached") : ""}
              placement="top"
            >
              <span>
                <Button
                  type="primary"
                  className="button button--primary user-management-button"
                  onClick={handleAddUser}
                  disabled={isUserLimitReached}
                >
                  <CirclePlus size={16} />
                  {t("userManagement.addUserButton")}
                </Button>
              </span>
            </StyledTooltip>
          </div>
        </div>

        <UserFilters
          searchText={filters.searchText}
          statusFilter={filters.statusFilter}
          roleFilter={filters.roleFilter}
          onSearchChange={filters.changeSearchText}
          onStatusChange={filters.changeStatusFilter}
          onRoleChange={filters.changeRoleFilter}
        />

        <CampaignTable
          rowKey="id"
          rowSelection={rowSelection}
          rowClassName={(record: User) => (IsUserLocked(record) ? "user-row-disabled" : "")}
          columns={getUserTableColumns({
            t,
            isArabic,
            deletingUserId,
            onAccessLevelChange: changeAccessLevel,
            onToggleStatus: toggleStatus,
            onEditUser: handleEditUser,
            onDeleteUser: setUserToDelete,
          })}
          data={filters.paginatedUsers}
          className="user-management-table"
          customPagination={
            <TablePagePagination
              currentPage={filters.displayedPage}
              totalPages={filters.totalPages}
              onPrevious={filters.goToPreviousPage}
              onNext={filters.goToNextPage}
            />
          }
        />
      </div>

      <ConfirmationModal
        modalOpen={isBulkDeleteOpen}
        onCancel={() => setIsBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title={t("userManagement.deleteSelectedTitle")}
        description={t("userManagement.deleteSelectedDescription")}
        icon={DELETE_ICON_URL}
        confirmText={t("userManagement.confirmDelete")}
        cancelText={t("userManagement.cancel")}
      />

      <ConfirmationModal
        modalOpen={Boolean(userToDelete)}
        onCancel={() => setUserToDelete(null)}
        onConfirm={handleConfirmDeleteUser}
        title={t("userManagement.deleteSingleTitle")}
        description={t("userManagement.deleteSingleDescription", { name: userToDelete?.name || "" })}
        icon={DELETE_ICON_URL}
        confirmText={t("userManagement.confirmDelete")}
        cancelText={t("userManagement.cancel")}
        confirmButtonProps={{ loading: Boolean(deletingUserId) }}
      />

      <UserFormModal
        mode="add"
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSubmitSuccess={addUser}
        apiKey={apiKey}
        tenantId={tenantId}
        isSocialListeningEnabled={isSocialListeningEnabled}
      />

      <UserFormModal
        mode="edit"
        isOpen={isEditUserModalOpen}
        user={userToEdit}
        onClose={() => setIsEditUserModalOpen(false)}
        onSubmitSuccess={handleUpdateUser}
        apiKey={apiKey}
        tenantId={tenantId}
        isSocialListeningEnabled={isSocialListeningEnabled}
      />
    </div>
  );
};

export default UserManagement;
